/**
 * Vocabulary profiles — CONFIGURATION OVER CODE (`infra/guides/CONFIG_OVER_CODE_KIT.md`).
 *
 * Two product terms depend on the type of institution using it:
 *   • the group of students — "cohort" (`cohorte`) at a course academy, "section"
 *     (`sección`) at a school, "group" (`grupo`) elsewhere;
 *   • the academic period — the progression path's `term` stage.
 *
 * ── Why the noun is NOT interpolated ────────────────────────────────────
 *
 * The solution that looks elegant —`` msg`${groupTerm} A` `` — BREAKS
 * agreement in Spanish. The article, gender and number change with the
 * word: `la cohorte` but `el grupo`, `Cohorte creada` but `Grupo creado`,
 * `Próximas cohortes` but `Próximos grupos`. With an interpolated noun you
 * end up writing `el sección` or `los cohorte` somewhere, and on top of
 * that the translator loses the whole sentence: they can't reorder it or
 * pick a different phrasing.
 *
 * The case that proves it beyond argument is in the profiles themselves:
 * `Cohort sections` is `Secciones de la cohorte`, but at a school it can't
 * be `Secciones de la sección` — it has to be `Apartados de la sección`.
 * No interpolation produces that.
 *
 * ── How it's done instead: catalog OVERLAY ──────────────────────
 *
 * Each profile contributes a JSON file that redefines ONLY the affected
 * messages, written out in full by hand, per locale. Every variant is
 * grammatical by construction because someone wrote it whole.
 *
 * ── Shape of the registry ───────────────────────────────────────────────────────
 *
 * Deliberately the SAME as the grading scheme registry
 * (`packages/shared/src/academy/grading/registry.ts`: `SCHEMES` +
 * `DEFAULT_GRADING_SCHEME_KEY` + `gradingScheme(key)` falling back to
 * default) and as the country packs one (`COUNTRY_PACKS` +
 * `DEFAULT_COUNTRY_CODE` + self-describing `FeatureManifest`). No second
 * configuration mechanism is invented.
 *
 * The difference, and it's on purpose: here the profiles are NOT written
 * in a TypeScript `Record`, they're DISCOVERED from `./profiles/*.json`.
 * Declaring a new profile means dropping a data file — zero lines of code,
 * which is what the rule demands. The active key comes from runtime config
 * (`VOCABULARY_PROFILE`), so switching profiles doesn't even require a recompile.
 *
 * ── What does NOT change ─────────────────────────────────────────────────────
 *
 * The database entity is still called `cohort`, and so are the API and the
 * sync rules. This is a presentation layer: no schema migration happens
 * over a word.
 */

import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import { compileMessage } from "@lingui/message-utils/compileMessage";
import type { SupportedLocale } from "@space/config/locales";
import conceptsData from "./concepts.json";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Self-describing metadata, same shape as the portfolio's `FeatureManifest`. */
export interface VocabularyManifest {
  key: string;
  name: string;
  description: string;
  scope: "platform" | "org" | "course";
  status: "stable" | "beta" | "experimental";
  since?: string;
}

/** A profile: identity + the catalog overlay, per locale. */
export interface VocabularyProfile {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly manifest: VocabularyManifest;
  /** `locale → { msgid (English, the catalog key) → full text }`. */
  readonly messages: Readonly<Record<string, Readonly<Record<string, string>>>>;
}

/** A governed concept: the contract EVERY profile must cover. */
export interface VocabularyConcept {
  readonly name: string;
  readonly description: string;
  readonly messages: readonly string[];
}

// ─── Contract (data, not code: `concepts.json`) ─────────────────────────────

const conceptsFile: { concepts: Record<string, VocabularyConcept> } = conceptsData;
const { concepts } = conceptsFile;

/** Concepts governed per profile, indexed by key. */
export const VOCABULARY_CONCEPTS: Readonly<Record<string, VocabularyConcept>> = concepts;

/**
 * All the `msgid`s a profile is REQUIRED to redefine, in every shipped
 * locale. It's the union of every concept's messages.
 */
export const GOVERNED_MESSAGES: readonly string[] = Object.values(concepts).flatMap(
  (c) => c.messages,
);

// ─── Profile discovery ──────────────────────────────────────────────

/**
 * Every `profiles/*.json` is a profile. The glob is what makes declaring a
 * new one NOT touch code: the file is added and it's already in the
 * registry, in the selector, and in the parametrized tests.
 */
const discovered = import.meta.glob<{ default: unknown }>("./profiles/*.json", { eager: true });

function str(bag: Record<string, unknown>, field: string, source: string): string {
  const v = bag[field];
  if (typeof v !== "string" || v.trim() === "") {
    throw new Error(`[vocabulary] ${source}: falta \`${field}\``);
  }
  return v;
}

/**
 * Validates a profile JSON and rebuilds it field by field. It's rebuilt
 * instead of cast because the file is written by a person (it's
 * CONFIGURATION): an `as VocabularyProfile` would wave through the missing
 * key and the empty table, and the failure would show up three layers
 * downstream, as a screen in English.
 */
function assertProfile(raw: unknown, source: string): VocabularyProfile {
  if (typeof raw !== "object" || raw === null) {
    throw new Error(`[vocabulary] ${source}: no es un objeto`);
  }
  const bag: Record<string, unknown> = { ...raw };
  const rawManifest = bag.manifest;
  if (typeof rawManifest !== "object" || rawManifest === null) {
    throw new Error(`[vocabulary] ${source}: falta \`manifest\``);
  }
  const rawMessages = bag.messages;
  if (typeof rawMessages !== "object" || rawMessages === null) {
    throw new Error(`[vocabulary] ${source}: falta \`messages\``);
  }
  const messagesBag: Record<string, unknown> = { ...rawMessages };
  const messages: Record<string, Record<string, string>> = {};
  for (const [locale, table] of Object.entries(messagesBag)) {
    if (typeof table !== "object" || table === null) {
      throw new Error(`[vocabulary] ${source}: \`messages.${locale}\` no es un objeto`);
    }
    const tableBag: Record<string, unknown> = { ...table };
    const entries: Record<string, string> = {};
    for (const [msgid, text] of Object.entries(tableBag)) {
      if (typeof text !== "string") {
        throw new TypeError(
          `[vocabulary] ${source}: \`messages.${locale}["${msgid}"]\` no es texto`,
        );
      }
      entries[msgid] = text;
    }
    messages[locale] = entries;
  }
  const manifestBag: Record<string, unknown> = { ...rawManifest };
  return {
    key: str(bag, "key", source),
    name: str(bag, "name", source),
    description: str(bag, "description", source),
    manifest: {
      key: str(manifestBag, "key", `${source}.manifest`),
      name: str(manifestBag, "name", `${source}.manifest`),
      description: str(manifestBag, "description", `${source}.manifest`),
      scope: "platform",
      status: "stable",
      since: typeof manifestBag.since === "string" ? manifestBag.since : undefined,
    },
    messages,
  };
}

function buildRegistry(): Record<string, VocabularyProfile> {
  const out: Record<string, VocabularyProfile> = {};
  for (const [path, mod] of Object.entries(discovered)) {
    const profile = assertProfile(mod.default, path);
    if (out[profile.key]) {
      throw new Error(`[vocabulary] clave de perfil duplicada: ${profile.key} (${path})`);
    }
    out[profile.key] = profile;
  }
  return out;
}

/** Profile registry indexed by key (== `COUNTRY_PACKS` / `SCHEMES`). */
export const VOCABULARY_PROFILES: Readonly<Record<string, VocabularyProfile>> = buildRegistry();

/** Stable profile order (for selects and catalog). */
export const VOCABULARY_PROFILE_KEYS: readonly string[] =
  Object.keys(VOCABULARY_PROFILES).toSorted();

/**
 * Default profile key (== `DEFAULT_SCHEME_KEY` / `DEFAULT_COUNTRY_CODE`).
 *
 * The owner specified it: this is an **online course academy**, not a
 * school. The group that studies together is a cohort — the catalog itself
 * defines it as "a scheduled edition of a course". `Sección` is the
 * Venezuelan school term and that's why it exists as a profile, but it
 * isn't the default.
 */
export const DEFAULT_VOCABULARY_PROFILE_KEY = "academia";

/** Returns the profile for a key, or the default if unknown (== `gradingScheme`). */
export function vocabularyProfile(key: string | null | undefined): VocabularyProfile {
  const k = (key ?? "").trim();
  const found = VOCABULARY_PROFILES[k];
  if (found) {
    return found;
  }
  const fallback = VOCABULARY_PROFILES[DEFAULT_VOCABULARY_PROFILE_KEY];
  if (!fallback) {
    throw new Error(
      `[vocabulary] falta el perfil por defecto "${DEFAULT_VOCABULARY_PROFILE_KEY}" en profiles/`,
    );
  }
  return fallback;
}

// ─── Overlay ready for `i18n.load` ────────────────────────────────────

/**
 * Translates a profile's overlay into the COMPILED catalog format: keys by
 * id-hash (`generateMessageId`, same as the macro) and already-compiled values.
 *
 * The compiling step isn't decoration. In a PRODUCTION build `@lingui/core`
 * does NOT compile messages it receives as a string: a raw `"Periodo {n}"`
 * would be painted literally with the braces. It's the same class of trap
 * as the fleet's id-hash bug: invisible in dev, broken in the binary.
 */
export function vocabularyOverlay(
  locale: SupportedLocale,
  profileKey?: string | null,
): Record<string, unknown> {
  const profile = vocabularyProfile(profileKey ?? activeVocabularyProfileKey());
  const table = profile.messages[locale];
  if (!table) {
    return {};
  }
  const out: Record<string, unknown> = {};
  for (const [msgid, text] of Object.entries(table)) {
    out[String(generateMessageId(msgid))] = compileMessage(text);
  }
  return out;
}

// ─── Active profile (runtime config, no recompile) ───────────────────────

interface VocabularyRuntimeConfig {
  VOCABULARY_PROFILE?: string;
}

/**
 * Active profile key. Comes from the SAME lever as `SERVER_URL` and
 * `POWERSYNC_URL` (`/config.js` written by nginx's entrypoint, falling back
 * to `import.meta.env.VITE_*` for Tauri and dev): switching profiles is a
 * container environment variable, not a new deployment.
 *
 * An unknown key does NOT blow up: it degrades to the default. A
 * misconfigured academy has to keep showing words, not id-hashes.
 */
export function activeVocabularyProfileKey(): string {
  const runtime =
    (globalThis as typeof globalThis & { __APP_CONFIG__?: VocabularyRuntimeConfig })
      .__APP_CONFIG__ ?? {};
  const candidates = [runtime.VOCABULARY_PROFILE, import.meta.env.VITE_VOCABULARY_PROFILE];
  const configured = candidates.find((v) => typeof v === "string" && v.trim() !== "")?.trim();
  return configured ?? DEFAULT_VOCABULARY_PROFILE_KEY;
}
